/**
 * Live map rules (Sprint 4): how the console classifies a vehicle from its latest known state.
 * Pure and runtime independent, so the console, reports and tests agree on one definition.
 */
import type { EventType } from "./icd/schemas.js";

/** Last known state of one device, as stored in public.device_latest (field names in camelCase). */
export type LiveSnapshot = {
  deviceId: string;
  vehicleId: string | null;
  /** Device time of the last telemetry/event that updated the row. */
  ts: string;
  /** Server time the last message arrived (null for rows touched only by events). */
  receivedAt: string | null;
  lat: number | null;
  lon: number | null;
  /** Time of the last position fix (effective device time); null = no fix yet. */
  posAt: string | null;
  posAcc: number | null;
  posSrc: string | null;
  speedKmh: number | null;
  ignition: boolean | null;
  seat: boolean | null;
  belt: boolean | null;
  /** Card UID of the logged-in operator (personal data, KVKK). Shown masked. */
  operatorUid: string | null;
  link: string | null;
  rssi: number | null;
  supplyV: number | null;
  lastEventType: string | null;
  lastEventAt: string | null;
  /** Newest alarm-type event; kept apart so a later card login does not hide an impact. */
  lastAlarmType: string | null;
  lastAlarmAt: string | null;
  /** Battery module (ICD v0.2 section 9); absent/null without the module. Last known reading, kept across messages without one. */
  soc?: number | null;
  batV?: number | null;
  /** + charge, - discharge (A), ICD v0.2 section 9. */
  batA?: number | null;
  batT?: number | null;
  /** Effective device time of that battery reading. */
  batAt?: string | null;
};

export const LIVE_STATES = ["working", "idle", "off", "offline"] as const;
export type LiveState = (typeof LIVE_STATES)[number];

/** Event types that colour a vehicle as "alarm" for a while. Impact never blocks the vehicle (ADR 0003). */
export const ALARM_EVENT_TYPES: readonly EventType[] = [
  "impact",
  "tamper",
  "seat_violation",
  "belt_violation",
  "power_loss",
  // ICD v0.2 section 9 (ADR 0017): module alarms.
  "overload",
  "deep_discharge",
  "bat_overtemp",
];

export const LIVE_RULES = {
  /** No message for this long = offline (same as mark_stale_devices_offline's default). */
  offlineAfterMs: 10 * 60_000,
  /** A position older than this while the device still talks = "last known position" (shown faded). */
  positionStaleAfterMs: 2 * 60_000,
  /** An alarm-type event keeps the vehicle red this long. Sprint 6 replaces this with open alarms. */
  alarmWindowMs: 15 * 60_000,
  /** Below this speed with ignition on = idling. */
  idleBelowKmh: 1,
} as const;

export type LiveView = {
  state: LiveState;
  /** A recent alarm-type event; shown on top of the state (red ring). */
  alarm: boolean;
  /** The vehicle can be placed on the map. */
  hasPosition: boolean;
  /** Placed at its last known position, which is not current (indoors, GNSS lost, offline). */
  positionStale: boolean;
};

const ms = (iso: string | null): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

/**
 * Classify a snapshot at `now`. `deviceOnline` is the devices.online flag (broker presence);
 * a device that is flagged online but silent past the offline window still counts as offline.
 */
export function classifyLive(s: LiveSnapshot, deviceOnline: boolean, now: Date, rules = LIVE_RULES): LiveView {
  const t = now.getTime();
  const lastHeard = ms(s.receivedAt) ?? ms(s.ts);
  const offline = !deviceOnline || lastHeard === null || t - lastHeard > rules.offlineAfterMs;

  const isAlarmType = (type: string | null) => type !== null && (ALARM_EVENT_TYPES as readonly string[]).includes(type);
  // Prefer the dedicated alarm columns; fall back to the last event for rows written before they existed.
  const alarmAt = isAlarmType(s.lastAlarmType) ? ms(s.lastAlarmAt) : isAlarmType(s.lastEventType) ? ms(s.lastEventAt) : null;
  // Event times are device times: allow a small clock lead, ignore events "from the future".
  const alarm = alarmAt !== null && t - alarmAt <= rules.alarmWindowMs && alarmAt <= t + 60_000;

  let state: LiveState;
  if (offline) state = "offline";
  else if (s.ignition !== true) state = "off";
  else if ((s.speedKmh ?? 0) >= rules.idleBelowKmh) state = "working";
  else state = "idle";

  const hasPosition = s.lat !== null && s.lon !== null && Number.isFinite(s.lat) && Number.isFinite(s.lon);
  // Age of the fix in DEVICE time (last message time minus fix time), so a device clock that runs
  // slow or fast does not fade a fresh position; the offline check above uses server time.
  const posAt = ms(s.posAt);
  const lastTs = ms(s.ts);
  const positionStale =
    hasPosition && (offline || posAt === null || lastTs === null || lastTs - posAt > rules.positionStaleAfterMs);

  return { state, alarm, hasPosition, positionStale };
}

/** Card UIDs are personal data: show only the last four characters. */
export function maskCardUid(uid: string | null): string | null {
  if (!uid) return null;
  return uid.length <= 4 ? "••••" : `••••${uid.slice(-4)}`;
}
